import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import SecurePaymentBadges from './SecurePaymentBadges';

/**
 * Outer layout for routes mounted under the marketing/dashboard tree.
 *
 * The marketing footer + the public Header are shown on the marketing
 * surface (home, /hosting, /vps, /email, /login, /about, /contact, the
 * checkout flows). Dashboard surfaces (any path under /dashboard, plus
 * the dedicated /vps-dashboard, /email-dashboard, /customer/* shells)
 * use DashShell — their own full-screen aurora background + sidebar.
 * Showing the marketing footer there fights the dashboard chrome and
 * looks broken, so we skip both Header and Footer on those routes.
 *
 * The full-screen console routes (/vnc, /term) skip this layout entirely
 * via their own <Route> entries in App.tsx — included here only as a
 * defensive guard in case future routes get added under Layout by
 * accident.
 */
const DASHBOARD_PATH_PREFIXES = [
  '/dashboard',
  '/vps-dashboard',
  '/email-dashboard',
  '/customer/',
  '/vnc/',
  '/term/',
];

const Layout: React.FC = () => {
  const { pathname } = useLocation();
  const isDashboard = DASHBOARD_PATH_PREFIXES.some(p => pathname === p.replace(/\/$/, '') || pathname.startsWith(p));

  if (isDashboard) {
    // Dashboard pages own their full chrome via DashShell. No header, no footer
    // from this outer layout — render the page directly into the viewport.
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-grow">
        <Outlet />
      </main>
      {/* Site-wide payment trust badges, shown above the footer on every
          marketing/checkout page (dashboard surfaces return earlier). */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <SecurePaymentBadges />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Layout;
